import { AlertTriangle, Loader2, RefreshCw, X } from 'lucide-react';
import { Button } from '../components/atoms/Button';

interface Props {
  /** Human-readable version string from the server (e.g. "2"). */
  latestVersion: string | null;
  busy?: boolean;
  onReload: () => void;
  onDismiss: () => void;
}

/**
 * "Newer version available" banner shown under the header when the chat's
 * most recent DOCX version doesn't match what the plugin last applied to
 * this document. Dismissable per-version (key stored in document settings)
 * so users don't get nagged after declining once.
 */
export function OutdatedVersionBanner({
  latestVersion,
  busy,
  onReload,
  onDismiss,
}: Props): JSX.Element {
  return (
    <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900">
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium">
          A newer version of this draft is available{latestVersion ? ` (v${latestVersion})` : ''}.
        </div>
        <div className="text-[10px] text-amber-800/80 mt-0.5 leading-snug">
          Reload to pull the latest from the server. Any unsaved local edits will be lost.
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-6 text-[10px] gap-1 border-amber-300"
          onClick={onReload}
          disabled={busy}
        >
          {busy ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
          Reload
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onDismiss}
          disabled={busy}
          title="Dismiss"
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
